import type { ReactNode } from "react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router";
import FrontendLoggerFactory from "utils/logging/FrontendLoggerFactory";

import { AdminGlobalSearchModal } from "@/components/layout/AdminGlobalSearchModal";
import { usePageLayoutShellAccess } from "@/components/layout/usePageLayoutShellAccess";
import { adminUiCopy } from "@/copy/adminUiCopy";
import { useAdminLogout } from "@/hooks/useAdminLogout";
import {
  findAdminNavLocation,
  getAdminNavBreadcrumbs,
  getAdminNavOriginPathname,
  isAdminNavPageOwnRoute,
} from "@/routing/adminNavModelQueries";
import {
  type AdminRouteAccessArgs,
  findAdminRouteByPathname,
} from "@/routing/adminRoutes";
import { useGetAdminV2MeQuery } from "@/store/api/baseApi";
import { getAdminBuildInfo } from "@/utils/buildInfo";
import {
  resolveAdminDeployStage,
  shouldShowStagingAdminWarning,
} from "@/utils/deployEnvironment";
import { isAdminScreenshotModeEnabled } from "@/utils/localDevChromeVisibility";
import { getVNextSidebarNavigation } from "@/vnext/app-shell/navigationModel";
import {
  filterScreenshotSafeNavigationItems,
  SCREENSHOT_UNSAFE_NAVIGATION_PATHS,
} from "@/vnext/app-shell/shellScreenshotSafety";
import { ShellSidebar } from "@/vnext/app-shell/ShellSidebar";
import {
  ShellToolPanels,
  useShellToolPanels,
} from "@/vnext/app-shell/ShellToolPanels";
import { ShellTopBar } from "@/vnext/app-shell/ShellTopBar";
import { StagingAdminWarning } from "@/vnext/app-shell/StagingAdminWarning";
import { usePinnedPages } from "@/vnext/app-shell/usePinnedPages";
import { useShellNavCollapse } from "@/vnext/app-shell/useShellNavCollapse";
import { VNextNotice } from "@/vnext/primitives";
import { cx } from "@/vnext/shared/cx";
import { DrawerOpenContext } from "@/vnext/shared/DrawerOpenContext";
import {
  AdminPageTitle,
  type AdminPageTitleProminence,
} from "@/vnext/shared/page-title/AdminPageTitle";
import { VNextToastProvider } from "@/vnext/shared/VNextToastProvider";

import "@/vnext/theme/vnext.css";
import "@/vnext/app-shell/shellChrome.css";

const logger =
  FrontendLoggerFactory.getLogger().withComponent("AdminVNextShell");

const formatDeployStageLabel = (deployStage: string): string =>
  deployStage === "localhost"
    ? "Local"
    : deployStage.charAt(0).toUpperCase() + deployStage.slice(1);

interface AdminVNextShellProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: Array<{
    label: string;
    path?: string;
  }>;
  actions?: ReactNode;
  children: ReactNode;
  contentClassName?: string;
  /**
   * @deprecated The workspace pills were removed (Nav PRD §11: the sidebar
   * is the only menu). Kept so existing routes still compile.
   */
  hideWorkspaceSections?: boolean;
  /** List workspaces pin their own toolbar while the shell header scrolls away. */
  scrollTopbar?: boolean;
  /** Authoring workspaces supply their own object-level h1. Safety chrome remains. */
  hidePageHeader?: boolean;
  /** A route-owned definite denial already explains its access failure. */
  hideAccessDataErrorBanner?: boolean;
  pageTitleProminence?: AdminPageTitleProminence;
}

/** CRM record pages, whose breadcrumb is "CRM › [record name]". */
const CRM_RECORD_ROUTE_IDS: ReadonlySet<string> = new Set([
  "v2_crm_contact_record",
  "user_details",
  "organisation_details",
  "v2_crm_business_record",
  "partner_details",
  "v2_crm_firm_record",
  "v2_crm_firm_client_record",
]);

const formatBreadcrumbs = (
  pathname: string,
  search: string,
  breadcrumbs: Array<{ label: string; path?: string }> | undefined,
  accessArgs: AdminRouteAccessArgs,
) => {
  // Section › Page › Tab from the navigation map (F-019) wins over the
  // older workspace crumbs a page supplies. On a member route (a record, a
  // detail page) the page's own last crumb, such as a record name, replaces
  // the generic leaf. A CRM record reads "CRM › [record name]" (Record
  // pages PRD, and each dashboard's Open profile row). A route outside the
  // map keeps the page's crumbs. A workbench opened from a dashboard reads
  // under that dashboard, e.g. "CRM › Users › Duplicate review" (C7-CT).
  const originPathname = getAdminNavOriginPathname(pathname, search);
  const suppliedLeaf = breadcrumbs?.[breadcrumbs.length - 1]?.label;
  if (originPathname) {
    const originCrumbs = getAdminNavBreadcrumbs(originPathname, accessArgs);
    if (originCrumbs.length > 0)
      return suppliedLeaf
        ? [...originCrumbs, { label: suppliedLeaf }]
        : originCrumbs;
  }
  const navCrumbs = getAdminNavBreadcrumbs(pathname, accessArgs);
  if (navCrumbs.length > 0) {
    // The source's Archived contacts view shares the dashboard route but
    // has its own leaf. Do not rename unrelated query-driven pages/records.
    if (
      (pathname === "/crm" || pathname === "/crm/contacts") &&
      new URLSearchParams(search).get("view") === "archived"
    ) {
      return [navCrumbs[0], { label: "Archived contacts" }];
    }
    const location = findAdminNavLocation(pathname);
    const page = location?.page ?? null;
    const isMemberRoute =
      location !== null &&
      page !== null &&
      !location.tab &&
      !isAdminNavPageOwnRoute(page, location.route.id);
    if (!isMemberRoute || !suppliedLeaf) return navCrumbs;
    return CRM_RECORD_ROUTE_IDS.has(location.route.id)
      ? [navCrumbs[0], { label: suppliedLeaf }]
      : [...navCrumbs.slice(0, -1), { label: suppliedLeaf }];
  }

  if (breadcrumbs && breadcrumbs.length > 0) {
    return breadcrumbs;
  }

  const route = findAdminRouteByPathname(pathname);
  return route ? [{ label: route.label, path: route.path }] : [];
};

const resolveStaffUserLabel = (staffUser?: {
  displayName?: string | null;
  email?: string | null;
}): string => {
  const displayName = staffUser?.displayName?.trim();
  if (displayName) return displayName;
  const email = staffUser?.email?.trim();
  if (email) return email;
  return "Admin operator";
};

/**
 * Mission Control's shell (Nav PRD §3): the sidebar is the only menu, the
 * one-row top bar carries the breadcrumb and tools, and every destination
 * comes from the navigation map's access-checked queries.
 */
export const AdminVNextShell = ({
  title,
  subtitle,
  breadcrumbs,
  actions,
  children,
  contentClassName,
  scrollTopbar = false,
  hidePageHeader = false,
  hideAccessDataErrorBanner = false,
  pageTitleProminence,
}: AdminVNextShellProps) => {
  const location = useLocation();
  const pathname = location.pathname;
  const shellAccess = usePageLayoutShellAccess();
  const screenshotMode = isAdminScreenshotModeEnabled();
  const { data: meData } = useGetAdminV2MeQuery(undefined, {
    // Screenshot fixtures are local/test-only and intentionally self-contained.
    // Avoid a live identity request turning a governed redacted capture into an
    // access-denied modal; real Admin routes keep the identity contract.
    skip: screenshotMode,
  });
  const { buildLabel } = getAdminBuildInfo();
  const showStagingWarning = useMemo(() => shouldShowStagingAdminWarning(), []);
  const deployStageLabel = useMemo(
    () =>
      screenshotMode
        ? "Staging"
        : formatDeployStageLabel(resolveAdminDeployStage()),
    [screenshotMode],
  );
  const staffUser = meData?.staffUser;
  const adminId = staffUser?.firebaseUid ?? null;
  const nav = useShellNavCollapse(adminId);
  const [narrowNav, setNarrowNav] = useState({ open: false, pathname });
  const narrowNavOpen = narrowNav.open && narrowNav.pathname === pathname;
  const [recordSearch, setRecordSearch] = useState<string | null>(null);
  const sidebarId = useId();
  const narrowNavButtonRef = useRef<HTMLButtonElement | null>(null);
  const narrowNavCloseButtonRef = useRef<HTMLButtonElement | null>(null);
  const hasOpenedNarrowNavRef = useRef(false);
  const { logout, isLoggingOut } = useAdminLogout();

  const routeAccess = useMemo<AdminRouteAccessArgs>(
    () => ({
      adminV2FoundationsEnabled: shellAccess.adminV2FoundationsEnabled,
      permissionKeys: shellAccess.permissionKeys,
      menuVisibility: shellAccess.effectiveMenuVisibility,
    }),
    [
      shellAccess.adminV2FoundationsEnabled,
      shellAccess.effectiveMenuVisibility,
      shellAccess.permissionKeys,
    ],
  );
  const hiddenPaths = screenshotMode
    ? SCREENSHOT_UNSAFE_NAVIGATION_PATHS
    : undefined;
  const sidebarItems = useMemo(() => {
    const items = getVNextSidebarNavigation(pathname, routeAccess);
    return screenshotMode ? filterScreenshotSafeNavigationItems(items) : items;
  }, [pathname, routeAccess, screenshotMode]);
  // Breadcrumb arrays are frequently rebuilt by route components. Memoize the
  // resolved value so diagnostics only react to real navigation changes.
  const resolvedBreadcrumbs = useMemo(
    () =>
      formatBreadcrumbs(pathname, location.search, breadcrumbs, routeAccess),
    [breadcrumbs, location.search, pathname, routeAccess],
  );
  const pins = usePinnedPages({ adminId, pathname, routeAccess, hiddenPaths });
  const panels = useShellToolPanels({ currentUserId: adminId, screenshotMode });
  const panelId = useId();
  // Record search keeps its existing gate: Admin V2 foundations and search.view.
  const canSearchRecords =
    shellAccess.adminV2FoundationsEnabled &&
    shellAccess.permissionKeys?.has("search.view") === true;

  const identity = useMemo(
    () =>
      screenshotMode
        ? {
            label: "redacted admin",
            email: "redacted",
            userId: "redacted",
            profilePhotoUrl: null,
          }
        : {
            label: resolveStaffUserLabel(staffUser),
            email: staffUser?.email ?? adminUiCopy.common.notAvailable,
            userId: staffUser?.firebaseUid ?? adminUiCopy.common.notAvailable,
            profilePhotoUrl: staffUser?.profilePhotoUrl,
          },
    [screenshotMode, staffUser],
  );

  const toggleNarrowNav = useCallback(() => {
    setNarrowNav((previous) => ({
      open: previous.pathname === pathname ? !previous.open : true,
      pathname,
    }));
  }, [pathname]);
  const closeNarrowNav = useCallback(() => {
    setNarrowNav((previous) =>
      previous.open ? { ...previous, open: false } : previous,
    );
  }, []);

  useEffect(() => {
    if (narrowNavOpen) {
      hasOpenedNarrowNavRef.current = true;
      narrowNavCloseButtonRef.current?.focus();
      return undefined;
    }
    if (hasOpenedNarrowNavRef.current) narrowNavButtonRef.current?.focus();
    return undefined;
  }, [narrowNavOpen]);

  useEffect(() => {
    if (!narrowNavOpen) return undefined;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) {
        event.preventDefault();
        closeNarrowNav();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [closeNarrowNav, narrowNavOpen]);

  useEffect(() => {
    logger.debug("Admin shell navigation state resolved", {
      operation: "admin_shell_navigation_state",
      pathname,
      title,
      breadcrumbCount: resolvedBreadcrumbs.length,
      sidebarItemCount: sidebarItems.length,
      adminV2FoundationsEnabled: shellAccess.adminV2FoundationsEnabled,
      permissionKeyCount: shellAccess.permissionKeys?.size ?? null,
      hasShellAccessDataError: shellAccess.hasShellAccessDataError,
      shellAccessCorrelationId: shellAccess.shellAccessCorrelationId,
    });
  }, [
    pathname,
    resolvedBreadcrumbs.length,
    shellAccess.adminV2FoundationsEnabled,
    shellAccess.hasShellAccessDataError,
    shellAccess.permissionKeys,
    shellAccess.shellAccessCorrelationId,
    sidebarItems.length,
    title,
  ]);

  const railCollapsed = nav.collapsed && !narrowNavOpen;

  return (
    <div
      className={cx(
        "vnext-shell",
        pathname.startsWith("/live-alerts") && "vnext-shell--live-alerts",
        scrollTopbar && "vnext-shell--scrolling-topbar",
        nav.collapsed && "vnext-shell--nav-collapsed",
        narrowNavOpen && "vnext-shell--narrow-nav-open",
        showStagingWarning && "vnext-shell--staging-warning",
        panels.active && "vnext-shell--panel-open",
      )}
    >
      <a className="vnext-skip-link" href="#admin-main-content">
        Skip to main content
      </a>

      <StagingAdminWarning visible={showStagingWarning} />

      {narrowNavOpen ? (
        <button
          aria-label="Close navigation overlay"
          className="vnext-sidebar-backdrop"
          onClick={closeNarrowNav}
          type="button"
        />
      ) : null}

      <ShellSidebar
        account={{
          buildLabel,
          environmentLine: `${deployStageLabel} · ${adminUiCopy.shellChrome.buildLabelPrefix}`,
          identity,
          isLoggingOut,
          onSignOut: () => void logout(),
        }}
        canReadLiveAlerts={
          shellAccess.permissionKeys?.has("admin.v2.access") === true
        }
        canSearchRecords={canSearchRecords}
        collapsed={railCollapsed}
        hiddenPaths={hiddenPaths}
        id={sidebarId}
        items={sidebarItems}
        onCloseNarrow={closeNarrowNav}
        onSearchRecords={setRecordSearch}
        ref={narrowNavCloseButtonRef}
        routeAccess={routeAccess}
      />

      <div className="vnext-main">
        <ShellTopBar
          breadcrumbs={resolvedBreadcrumbs}
          navCollapsed={nav.collapsed}
          narrowNavOpen={narrowNavOpen}
          onToggleCollapsed={nav.toggle}
          onToggleNarrowNav={toggleNarrowNav}
          panelId={panelId}
          panels={panels}
          pins={pins}
          ref={narrowNavButtonRef}
          sidebarId={sidebarId}
        />

        <main className="vnext-canvas" id="admin-main-content" tabIndex={-1}>
          <div className={cx("vnext-content", contentClassName)}>
            {shellAccess.hasShellAccessDataError &&
            !shellAccess.suppressOutageAccessBanner &&
            !hideAccessDataErrorBanner ? (
              <VNextNotice
                action={
                  <button
                    className="vnext-notice__link"
                    onClick={shellAccess.refetchShellAccessData}
                    type="button"
                  >
                    {adminUiCopy.common.retry}
                  </button>
                }
                tone="warning"
              >
                {adminUiCopy.shellAccess.degradedBanner}
                {shellAccess.shellAccessCorrelationId ? (
                  <span className="vnext-notice__meta">
                    {adminUiCopy.common.correlationIdLabel}:{" "}
                    {shellAccess.shellAccessCorrelationId}
                  </span>
                ) : null}
              </VNextNotice>
            ) : null}

            {hidePageHeader ? null : pageTitleProminence ? (
              <AdminPageTitle
                actions={actions}
                prominence={pageTitleProminence}
                subtitle={subtitle}
                title={title}
              />
            ) : (
              <div className="vnext-page-header">
                <div>
                  <h1 className="vnext-page-header__title">{title}</h1>
                  {subtitle ? (
                    <p className="vnext-page-header__subtitle">{subtitle}</p>
                  ) : null}
                </div>
                {actions ? (
                  <div className="vnext-page-header__actions">{actions}</div>
                ) : null}
              </div>
            )}

            <DrawerOpenContext.Provider value={panels.dismissForDrawer}>
              <VNextToastProvider>{children}</VNextToastProvider>
            </DrawerOpenContext.Provider>
          </div>
        </main>
      </div>

      <ShellToolPanels
        active={panels.active}
        available={panels.available}
        currentUserId={adminId ?? undefined}
        key={adminId}
        onClose={panels.close}
        panelId={panelId}
      />

      {canSearchRecords ? (
        <AdminGlobalSearchModal
          initialQuery={recordSearch ?? ""}
          key={recordSearch ?? "closed"}
          onClose={() => setRecordSearch(null)}
          open={recordSearch !== null}
          routeAccess={routeAccess}
        />
      ) : null}
    </div>
  );
};
